import { Common } from 'ontraccr-common';
import * as Sentry from '@sentry/react';

export default {};

const NATIVE_FORM_FIELDS = {
  employeeName: {
    value: 'Employee Name',
    key: 'employeeName',
  },
  employeeId: {
    value: 'Employee ID',
    key: 'employeeId',
  },
  formDate: {
    value: 'Submission Date',
    key: 'formDate',
  },
  formNumber: {
    value: 'Form Number',
    key: 'formNumber',
  },
};

export const mapV1FormulaToV2 = ({
  formula,
  tags = [],
  fieldMap = {},
  sectionMap = {},
  id,
  calculationColumn,
}) => {
  if (!formula?.length || !tags?.length) return formula;
  let newFormula = formula;
  let offset = 0;
  tags.forEach((tag) => {
    const {
      fieldId,
      startIndex,
      endIndex,
      sectionId,
      colKey,
      nativeKey,
    } = tag;
    let newText;
    if (nativeKey) {
      newText = `${NATIVE_FORM_FIELDS[nativeKey]?.value}`;
      if (!newText) return;
    } else {
      const section = sectionMap[sectionId];
      if (!section) return;
      const field = fieldMap[fieldId];
      if (!field) return;
      const { name: sectionName } = section;
      const { configProps: { title, columns = [] } = {} } = field ?? {};
      if (!title) return;
      const isTableCalculation = calculationColumn && fieldId === id;
      const ourColumn = columns.find(({ key }) => key === colKey);
      const colName = ourColumn?.name ?? colKey;
      const colKeySuffix = isTableCalculation ? colName : `Sum of ${colName} values`;
      const suffix = colKey ? ` - ${colKeySuffix}` : '';
      newText = `${sectionName} - ${title.trim()}${suffix}`;
    }
    const middleText = `[[${newText}]]`;
    newFormula = `${newFormula.slice(0, startIndex + offset)}${middleText}${newFormula.slice(endIndex + 1 + offset)}`;
    offset += middleText.length - (endIndex - startIndex + 1);
  });
  return newFormula;
};

export const cleanTagValue = (value) => value.replace(/[\u00A0\u200B\r\n]+/g, '');

export const convertTagifyTagsToOntraccrTags = ({
  tags,
  value: stringValue,
  fieldMap = {},
}) => {
  const tagValueMap = Common.getIdMap(
    tags.map((tag) => ({ ...tag, value: cleanTagValue(tag.value) })),
    'value',
  );

  // Algorithm to match tags inside [[ and ]] and replace with tag values
  const tagPattern = /\[\[([^\]]+)\]\]/g;

  // Use matchAll to get all matches with their indices
  const matches = Array.from(stringValue.matchAll(tagPattern));
  let result = stringValue;
  let offset = 0;
  const foundTags = [];

  // Process matches in forward order and maintain offset
  matches.forEach((match) => {
    const originalStart = match.index;
    const originalEnd = match.index + match[0].length;
    const adjustedStart = originalStart + offset;
    const adjustedEnd = originalEnd + offset;
    const tagKey = match[1]; // The captured group

    // Remove any whitespace from the tag key
    const cleanTagKey = tagKey.trim();

    try {
      const { value } = JSON.parse(cleanTagKey);
      // Check if the tag exists in our tagValueMap
      if (tagValueMap[value]?.value) {
        const { key: tagId } = tagValueMap[value];
        const [realFieldId, colKey] = tagId.split('/');
        const field = fieldMap[realFieldId];
        const nativeField = NATIVE_FORM_FIELDS[realFieldId]; // for form collected fields
        const replacement = `#${tagValueMap[value].value.trim()}`;
        const oldLength = match[0].length;
        const newLength = replacement.length;
        result = result.slice(0, adjustedStart) + replacement + result.slice(adjustedEnd);

        // Update offset for subsequent matches
        offset += newLength - oldLength;

        // Add to foundTags with adjusted indices
        foundTags.push({
          startIndex: adjustedStart,
          endIndex: adjustedStart + newLength - 1,
          fieldId: field?.id,
          sectionId: field?.sectionId,
          colKey,
          supportsFilter: field?.selectedType === 'table' && field?.configProps?.dataType === 'TimeEntry',
          nativeKey: nativeField?.key,
        });
      }
    } catch (e) {
      Sentry.captureException(e);
    }
  });

  return { tags: foundTags, formula: result };
};
